// Shared WebSocket types

export interface WsCommand extends Record<string, unknown> {
  command: string;
}

export interface WsError extends Record<string, unknown> {
  error: true;
}

export enum WsPrefixes {
  mmPrefix = 'MM__WS',
  esPrefix = 'ES__WS',
  assistantPrefix = 'ASSISTANT__WS',
  authPrefix = 'AUTH__WS',
  smmPrefix = 'SMM__WS',
}

/**
 * Mutual-exclusion groups for WS channels. Channels configured with the same
 * group force-disconnect each other on connect. The MM and ES data channels
 * are mutually exclusive (only one domain's data socket is live at a time); the
 * assistant/auth invalidation channels belong to no group and stay connected
 * alongside whichever data channel is active.
 */
export enum WsExclusionGroups {
  /** The domain data channels (MM / ES) — only one may be connected at once. */
  data = 'DATA__WS',
}

export type WsConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnecting';

export interface WsConnectionState {
  status: WsConnectionStatus;
  error: WsError | null;
  isReconnecting: boolean;
}

// Shared WS JSON response types

export type JsonCommandOk = 'ok';
export type JsonCommandError = 'error';

/** The WS envelope `command` value marking a successful response. */
export const WS_OK_COMMAND: JsonCommandOk = 'ok';
/** The WS envelope `command` value marking a failed response. */
export const WS_ERROR_COMMAND: JsonCommandError = 'error';

/** Narrows a WS envelope's `command` field to the shared success literal. */
export const isOkCommand = (command: string): command is JsonCommandOk => command === WS_OK_COMMAND;

/** Narrows a WS envelope's `command` field to the shared failure literal. */
export const isErrorCommand = (command: string): command is JsonCommandError =>
  command === WS_ERROR_COMMAND;

export interface JsonWsSuccessResponse extends WsCommand {
  command: JsonCommandOk;
  okCommand: string;
}

export interface JsonSuccess extends JsonWsSuccessResponse {
  message?: string;
  optional?: string;
}

export interface JsonWsErrorResponse extends WsCommand {
  command: JsonCommandError;
  errorCommand: string;
}

export interface JsonFailure extends JsonWsErrorResponse {
  message?: string;
  optional?: string;
}

export type JsonListenResponse<T, V extends object | null | undefined> = V & {
  command: T;
};

export type RequestStatus = 'pending' | 'success' | 'error' | null;

/** The payload type carried by any `onMessage` action from either WS channel. */
export type WsMessagePayload = { message: string } & Record<string, unknown>;
